import { Component, Input, EventEmitter, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TextQuestion } from '@interfaces/questions';
import { Subscription } from 'rxjs';
import { WorkflowItemKeyDirective } from '@workflow/directives/workflow-item-key/workflow-item-key.directive';
import { InputService } from '@app/services/input/input.service';

interface ITranslationData {
	minLength: number;
	maxLength: number;
}

@Component({
	selector: 'workflow-text-question',
	templateUrl: './text-question.component.html',
	styleUrls: ['./text-question.component.scss'],
})
export class TextQuestionComponent implements OnChanges, OnInit, OnDestroy {

	@Input() question: TextQuestion;
	@Input() isWizard = false;
	@Input() finishedAndLocked = false;
	@Input() isLocked = false;

	@Output() valid: EventEmitter<boolean> = new EventEmitter();

	translationData: ITranslationData;
	errorMessage: string;
	selected: string | undefined;
	minLength: number;
	maxLength: number;

	private subscription = new Subscription();
	private componentInit = false;
	
	private readonly BACKEND_RESPONSE_TIMEOUT = 500;
	private undefinedTimestamp = 0;

	constructor(
		private workflowItemKey: WorkflowItemKeyDirective,
		private inputService: InputService) {	}

	ngOnInit(): void {
		this.subscription.add(this.workflowItemKey.property$.subscribe(data => {
			if (!this.finishedAndLocked) {
				if (!this.componentInit) {
					this.selected = data as string;
					this.componentInit = true;
				} else {
					if (Date.now() - this.undefinedTimestamp < this.BACKEND_RESPONSE_TIMEOUT) {
						this.selected ??= data as string;
					} else {
						this.selected = data as string;
					}
				}
				this.errorMessage = this.buildErrorString();
				this.testValidity();
			}
		}));
		this.subscription.add(this.workflowItemKey.propertyFull$.subscribe(data => {
			if (this.finishedAndLocked) {
				this.selected = typeof (data) === 'string' ? data as string : undefined;
				this.valid.emit(true);
			}
		}));
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		this.inputService.suspendDetectionSwipe = false;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.question) {
			this.minLength = this.question?.minLength;
			this.maxLength = this.question?.maxLength;
			this.translationData = this.buildTranslationData();
			this.errorMessage = this.buildErrorString();
		}
	}

	private buildErrorString(): string {
		if (!this.minLength && !this.selected && !this.question?.isAnswerOptional) {
			return 'input.string.error.required';
		} else if (this.minLength > 0 && this.maxLength > 0) {
			return 'input.string.error.length.minMax';
		} else if (this.minLength > 0) {
			return 'input.string.error.length.min';
		} else if (this.maxLength > 0) {
			return 'input.string.error.length.max';
		}
		return 'input.string.error.length.unknownError';
	}

	private buildTranslationData(): ITranslationData {
		return {
			minLength: this.minLength,
			maxLength: this.maxLength,
		};
	}

	onTextChange(): void {
		if (!this.finishedAndLocked) {
			this.errorMessage = this.buildErrorString();
			const validity = this.testValidity();
			if (!validity) {
				this.undefinedTimestamp = Date.now();
			}
			this.workflowItemKey.setProperty(validity ? this.selected : undefined);
			
		}
	}

	enableSwipe(): void {
		this.inputService.suspendDetectionSwipe = false;
	}

	disableSwipe(): void {
		this.inputService.suspendDetectionSwipe = true;
	}

	private testValidity(): boolean {
		let valid = !!this.selected;
		if (this.minLength && !this.maxLength) {
			valid = this.selected?.length >= this.minLength;
		} else if (!this.minLength && this.maxLength) {
			valid = this.selected?.length <= this.maxLength;
		} else if (this.minLength && this.maxLength) {
			valid = this.selected?.length >= this.minLength && this.selected?.length <= this.maxLength;
		}
		if (this.question.isAnswerOptional && !this.selected) {
			valid = true;
		}
		this.valid.emit(valid);
		return valid;
	}
}
